/**
 * Vista completa de actividad para estudiantes
 * Muestra tarea, objetivos y permite iniciar la actividad
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { TeacherActivity } from '../../types';
import { academicService, UserLessonProgress, ObjectiveStatus } from '../../services/academicService';
import configService from '../../services/configService';
import webSocketService from '../../services/webSocketService';
import { useScreenOrientation } from '../../hooks/useScreenOrientation';
import EvaluationResults from '../Academic/EvaluationResults';
import './StudentActivityView.css';

interface StudentActivityViewProps {
  activity: TeacherActivity;
  onStartActivity?: () => void;
  onClose?: () => void;
  onBack?: () => void; // Callback para volver a la lista de actividades
}

const StudentActivityView: React.FC<StudentActivityViewProps> = ({
  activity,
  onStartActivity,
  onClose,
  onBack,
}) => {
  const [starting, setStarting] = useState(false);
  const [progress, setProgress] = useState<UserLessonProgress | null>(null);
  const [isActivityActive, setIsActivityActive] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [evaluationAutoShown, setEvaluationAutoShown] = useState(false);
  /** Una sola oportunidad de reintentar la actividad después de evaluada */
  const [hasUsedRetry, setHasUsedRetry] = useState(false);
  /** Ref para que loadProgress (p. ej. desde el interval) siempre vea si ya mostramos la evaluación y no reabra el overlay */
  const evaluationShownRef = useRef(false);
  const orientation = useScreenOrientation();

  // Nivel único por actividad (solo tache_id) para no mezclar progreso entre actividades del profesor
  const progressLevel = useMemo(
    () => (activity.tache_id ? `ACTIVIDAD_${activity.tache_id}` : 'ACTIVIDAD'),
    [activity.tache_id]
  );

  // Consultar progreso de la actividad
  const loadProgress = async () => {
    try {
      const config = configService.getConfig();
      const email = config.email;
      if (!email) return;

      const unit = 0; // Las actividades del profesor no tienen unit/lesson específicos
      const lesson = 0;

      const activityProgress = await academicService.getUserLessonProgress(email, progressLevel, unit, lesson);
      if (activityProgress) {
        setProgress(activityProgress);
        // No marcar isActivityActive desde el progreso guardado: "En progreso" solo al hacer clic en Iniciar/Reanudar en esta sesión

        // Si hay evaluación y todos los objetivos están completados, mostrar resultados solo si aún no los hemos mostrado (ref evita reabrir por closure obsoleto del interval)
        if (activityProgress.all_objectives_completed && activityProgress.rubric_evaluation && !evaluationShownRef.current) {
          evaluationShownRef.current = true;
          setShowResults(true);
          setEvaluationAutoShown(true);
        }
      } else {
        setProgress(null);
      }
    } catch (error) {
      console.error('[StudentActivityView] Error al cargar progreso:', error);
    }
  };

  // Si el WebSocket tiene esta actividad activa (misma sesión: dio Iniciar/Reanudar y luego salió del panel), mostrar "En progreso"
  const checkActivityStatus = () => {
    const academicMode = webSocketService.getAcademicMode();
    if (academicMode.isActive && academicMode.level === progressLevel &&
        academicMode.unit === 0 && academicMode.lesson === 0) {
      setIsActivityActive(true);
    }
  };

  // Cargar progreso al montar y al cambiar de actividad
  useEffect(() => {
    evaluationShownRef.current = false;
    setHasUsedRetry(false); // Cada actividad tiene una oportunidad de reintentar
    const initialize = async () => {
      checkActivityStatus();
      await loadProgress();
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.tache_id]);

  /** Reintentar la actividad una vez: resetea objetivos y evaluación, cierra el modal y recarga */
  const handleRetryActivity = async () => {
    const config = configService.getConfig();
    const email = config.email;
    if (!email) return;
    const unit = 0;
    const lesson = 0;
    const updated = await academicService.resetLessonProgress(email, progressLevel, unit, lesson);
    setHasUsedRetry(true);
    setShowResults(false);
    if (updated) setProgress(updated);
    await loadProgress();
  };


  // Refrescar progreso cada 1 segundo si la actividad está activa (para mayor reactividad)
  useEffect(() => {
    if (!isActivityActive) return;
    
    const interval = setInterval(() => {
      loadProgress();
    }, 1000); // Reducido a 1 segundo para mayor reactividad

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivityActive]);

  // NO resetear isActivityActive cuando se cierra la vista - mantener el estado persistente

  // Handler para actualizaciones de objetivos (llamado por WebSocket)
  const handleObjectiveUpdate = async (objectiveDescription: string, isCorrect: boolean) => {
    try {
      const config = configService.getConfig();
      const email = config.email;
      if (!email) return;

      const unit = 0;
      const lesson = 0;

      console.log('[StudentActivityView] Objetivo actualizado:', objectiveDescription, isCorrect);

      // Actualizar el estado del objetivo directamente
      const updatedProgress = await academicService.updateObjectiveStatus(
        email,
        progressLevel,
        unit,
        lesson,
        objectiveDescription,
        isCorrect
      );

      if (updatedProgress) {
        setProgress(updatedProgress);
        console.log('[StudentActivityView] ✅ Progreso actualizado:', {
          completed: Object.values(updatedProgress.objectives_status || {}).filter(
            (s) => s && typeof s === 'object' && (s as ObjectiveStatus).completed
          ).length,
          total: activity.objectives.length
        });

        // Verificar si todos los objetivos están completados
        if (updatedProgress.all_objectives_completed) {
          console.log('[StudentActivityView] 🎉 ¡Todos los objetivos completados!');
          
          // Si ya hay evaluación, mostrarla inmediatamente (solo si no se mostró antes)
          if (updatedProgress.rubric_evaluation && !evaluationAutoShown) {
            setShowResults(true);
            setEvaluationAutoShown(true);
          } else if (!updatedProgress.rubric_evaluation && !evaluationAutoShown) {
            // Disparar evaluación desde aquí por si el WebSocket no pudo hacerlo (p. ej. 406/409 en updateObjectiveStatus)
            console.log('[StudentActivityView] Disparando triggerEvaluationIfAllComplete (sin evaluación guardada)');
            webSocketService.triggerEvaluationIfAllComplete().then(() => {
              loadProgress();
            }).catch(() => {});
            // Polling de respaldo por si la evaluación tarda
            const checkEvaluationInterval = setInterval(async () => {
              const latestProgress = await academicService.getUserLessonProgress(
                email,
                progressLevel,
                unit,
                lesson
              );
              if (latestProgress?.rubric_evaluation && !evaluationAutoShown) {
                setProgress(latestProgress);
                setShowResults(true);
                setEvaluationAutoShown(true);
                clearInterval(checkEvaluationInterval);
              }
            }, 1000);
            setTimeout(() => clearInterval(checkEvaluationInterval), 30000);
          }
        }
      }
    } catch (error) {
      console.error('[StudentActivityView] Error al actualizar objetivo:', error);
      // Si falla, intentar recargar el progreso completo
      loadProgress();
    }
  };

  const handleStartActivity = async () => {
    setStarting(true);
    try {
      const config = configService.getConfig();
      const email = config.email;
      if (!email) {
        alert('No se encontró el email del estudiante.');
        return;
      }

      // Iniciar actividad: convertir a formato académico y activar modo académico
      await academicService.startTeacherActivity(
        activity,
        email,
        handleObjectiveUpdate, // Pasar el handler que actualiza el estado directamente
        () => loadProgress()   // Cuando termine la evaluación, refrescar para mostrar resultados
      );

      // Recargar progreso después de iniciar la actividad
      await loadProgress();
      
      // Activar el polling de progreso
      setIsActivityActive(true);

      // Notificar que la actividad ha comenzado
      if (onStartActivity) {
        onStartActivity();
      }
    } catch (error) {
      console.error('[StudentActivityView] Error al iniciar actividad:', error);
      alert('Error al iniciar la actividad. Por favor, intenta de nuevo.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className={`student-activity-view ${orientation}`}>
      {orientation === 'portrait' && onClose && (
        <div className="student-activity-view-header-portrait">
          <div className="student-activity-view-header-portrait-top">
            <button
              className="student-activity-view-back-button"
              onClick={onBack || onClose}
              aria-label="Volver"
              title="Volver"
            >
              <img 
                src="/assets/ui/iconos/Flecha_Atras.png" 
                alt="Volver"
                className="student-activity-view-back-icon"
              />
            </button>
            <h2>Actividad</h2>
            <button
              className="student-activity-view-close-button"
              onClick={onClose}
              aria-label="Cerrar"
            >
              <img 
                src="/assets/ui/botones/Boton_Cerrar.png" 
                alt="Cerrar"
                className="student-activity-view-close-icon"
              />
            </button>
          </div>
        </div>
      )}

      <div className="student-activity-view-header">
        <div className="student-activity-view-header-top">
          {onClose && (
            <button
              className="student-activity-view-back-button-landscape"
              onClick={onBack || onClose}
              aria-label="Volver"
              title="Volver"
            >
              <img 
                src="/assets/ui/iconos/Flecha_Atras.png" 
                alt="Volver"
                className="student-activity-view-back-icon"
              />
            </button>
          )}
          <h2>Actividad</h2>
          {orientation === 'landscape' && onClose && (
            <button
              className="student-activity-view-close-button-landscape"
              onClick={onClose}
              aria-label="Cerrar"
              title="Cerrar"
            >
              <img 
                src="/assets/ui/botones/Boton_Cerrar.png" 
                alt="Cerrar"
                className="student-activity-view-close-icon"
              />
            </button>
          )}
        </div>
        <div className="academic-mode-level">
          {activity.classroom_id ? `Aula ${activity.classroom_id}` : 'Actividad General'}
          {activity.level && ` - ${activity.level}`}
        </div>
      </div>

      <div className="student-activity-view-content academic-mode-content">
        {/* Tarea - mismo estilo que academic-mode-task-container */}
        <div className="academic-mode-task-container">
          <div className="academic-mode-task-header">
            <h3>Tâche</h3>
          </div>
          <div className="academic-mode-task-description">
            {activity.task_description}
          </div>
        </div>

        {/* Objetivos - mismo estilo que lesson-checklist */}
        <div className="lesson-checklist">
          <h3>Objetivos de la Lección</h3>
          <div className="objectives-list">
            {activity.objectives.map((objective, index) => {
              // Obtener estado del objetivo desde el progreso
              const getObjectiveStatus = (): ObjectiveStatus | null => {
                if (!progress || !progress.objectives_status) return null;
                const status = progress.objectives_status[objective.description];
                if (!status || typeof status !== 'object') return null;
                return status as ObjectiveStatus;
              };

              const objectiveStatus = getObjectiveStatus();
              
              const getProgressDisplay = (): string => {
                if (!objectiveStatus) return '';
                if (objectiveStatus.completed) return '✅';
                return '';
              };

              const getProgressClass = (): string => {
                if (!objectiveStatus) return 'objective-pending';
                if (objectiveStatus.completed) return 'objective-completed';
                const correctCount = objectiveStatus.correct_count ?? 0;
                if (correctCount > 0) return 'objective-in-progress';
                return 'objective-pending';
              };

              const progressDisplay = getProgressDisplay();
              const progressClass = getProgressClass();

              return (
                <div key={index} className={`objective-item ${progressClass}`}>
                  {progressDisplay && (
                    <div className="objective-header">
                      <span className="objective-progress">{progressDisplay}</span>
                    </div>
                  )}
                  <div className="objective-description">{objective.description}</div>
                </div>
              );
            })}
          </div>
          {progress && (
            <div className="lesson-progress-summary">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      (Object.values(progress.objectives_status || {}).filter((s) => s && typeof s === 'object' && (s as ObjectiveStatus).completed).length /
                        activity.objectives.length) *
                      100
                    }%`,
                  }}
                />
              </div>
              <div className="progress-text">
                {Object.values(progress.objectives_status || {}).filter((s) => s && typeof s === 'object' && (s as ObjectiveStatus).completed).length} /{' '}
                {activity.objectives.length} objetivos completados
              </div>
            </div>
          )}
        </div>

        {/* Botón o mensaje de completado - mismo estilo que academic-mode-start-button */}
        {progress?.all_objectives_completed ? (
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <div className="activity-completed-banner" style={{
              padding: '15px',
              backgroundColor: '#4CAF50',
              color: 'white',
              borderRadius: '8px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '18px',
              width: '100%'
            }}>
              🎉 ¡Actividad Completada!
            </div>
            <button
              className="academic-mode-start-button"
              onClick={() => {
                if (progress?.rubric_evaluation) {
                  setShowResults(true);
                  setEvaluationAutoShown(true);
                } else {
                  alert('No hay evaluación guardada para esta actividad. La evaluación se genera al completar todos los objetivos; si completaste la actividad antes, puede que no se haya guardado.');
                }
              }}
              style={{ maxWidth: '320px' }}
            >
              Ver resultado de evaluación
            </button>
          </div>
        ) : (
          (() => {
            const hasProgress = progress && !progress.all_objectives_completed &&
              Object.values(progress.objectives_status || {}).some(
                (s: any) => s && typeof s === 'object' && ((s as ObjectiveStatus).correct_count > 0 || (s as ObjectiveStatus).total_correct > 0)
              );
            const buttonText = starting
              ? (hasProgress ? 'Reanudando...' : 'Iniciando...')
              : isActivityActive
              ? 'Actividad en Progreso'
              : hasProgress
              ? 'Reanudar'
              : 'Iniciar Actividad';
            return (
              <button
                className="academic-mode-start-button"
                onClick={handleStartActivity}
                disabled={starting || isActivityActive}
              >
                {buttonText}
              </button>
            );
          })()
        )}
      </div>

      {/* Mostrar resultados de evaluación cuando estén disponibles */}
      {showResults && progress?.rubric_evaluation && (
        <div
          className="evaluation-results-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowResults(false);
            loadProgress();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="evaluation-results-overlay-content"
            style={{ maxWidth: '90%', maxHeight: '90%', overflow: 'auto', position: 'relative', zIndex: 1001 }}
            onClick={(e) => e.stopPropagation()}
          >
            <EvaluationResults
              evaluation={progress.rubric_evaluation}
              onClose={() => {
                setShowResults(false);
                loadProgress();
              }}
              onContinue={() => {
                setShowResults(false);
                loadProgress();
              }}
              onRetry={!hasUsedRetry ? handleRetryActivity : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentActivityView;
